import React, { useState } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { format } from 'date-fns';
import { Calendar, User, Tag, Clock, RefreshCw, Pencil } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { usePermissions } from '../contexts/PermissionsContext';
import { getContentTypeBadgeClasses } from '../utils/contentTypes';
import { LinkPillList } from './LinkPill';
import TaskTimeline from './TaskTimeline';
import TaskComments from './TaskComments';
import { TaskIdBadge } from './TaskIdBadge';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TaskDetailModal = ({ task, isOpen, onClose, users = [], onTaskUpdated, onEdit }) => {
  const { token, user } = useAuth();
  const { can } = usePermissions();
  const [isResubmitting, setIsResubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [commentCount, setCommentCount] = useState(null);
  const [linksOverride, setLinksOverride] = useState(null);

  // Pre-fetch the comment count so the tab shows "Comments (N)" before it's opened.
  React.useEffect(() => {
    let cancelled = false;
    setCommentCount(null);
    setLinksOverride(null);
    if (isOpen && task?.id && token) {
      axios.get(`${API}/tasks/${task.id}/comments`, { headers: { Authorization: `Bearer ${token}` } })
        .then((res) => { if (!cancelled) setCommentCount(res.data?.count ?? (res.data?.comments?.length || 0)); })
        .catch(() => {});
    }
    return () => { cancelled = true; };
  }, [isOpen, task?.id, token]);
  
  if (!task) return null;

  const assignedUser = users.find(u => u.id === task.task_done_by);

  // A comment containing a URL auto-adds it to Links on the backend — refetch
  // just this task so the Links section updates immediately, no page refresh.
  const handleLinksAdded = async () => {
    try {
      const res = await axios.get(`${API}/tasks/${task.id}`, { headers: { Authorization: `Bearer ${token}` } });
      setLinksOverride(res.data?.links || []);
      onTaskUpdated?.();
    } catch (error) {
      // Links will still appear next time the task list refreshes normally.
    }
  };

  const handleResubmitForApproval = async () => {
    setIsResubmitting(true);
    try {
      await axios.put(
        `${API}/tasks/${task.id}/resubmit-approval`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Task resubmitted for manager approval!');
      if (onTaskUpdated) {
        onTaskUpdated();
      }
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to resubmit task');
    } finally {
      setIsResubmitting(false);
    }
  };

  const handleEditClick = () => {
    if (onEdit) {
      onEdit(task);
      onClose();
    }
  };

  // Read-only role still gates via legacy user_type until Phase 5 — read-only is
  // not a permission, it's a user state. Keep until role_level is fully removed.
  const isReadOnly = user?.role_level === 'Read-only';

  // Phase 4 — Manager bypass replaced by RBAC `tasks.edit`. Owners and creators
  // retain edit rights regardless (legacy behavior).
  const canEdit = !isReadOnly && (
    can('tasks.edit') ||
    task.task_done_by === user?.id ||
    task.created_by === user?.id
  );

  // Phase 4 — resubmit gated by `tasks.approve` (manager-only action) or owner.
  const canResubmit = !isReadOnly && task.manager_approval_status === 'Rejected' && 
    (task.task_done_by === user?.id || can('tasks.approve'));

  const getStatusColor = (status) => {
    const colors = {
      'Backlog': 'bg-slate-100 text-slate-700 border-slate-200',
      'Completed': 'bg-green-100 text-green-700 border-green-200',
      'In Process': 'bg-blue-100 text-blue-700 border-blue-200',
      'Yet to start': 'bg-yellow-100 text-yellow-700 border-yellow-200',
      'On Hold': 'bg-red-100 text-red-700 border-red-200'
    };
    return colors[status] || colors['Yet to start'];
  };

  const getCategoryColor = (category) => {
    const colors = {
      'Technical SEO': 'bg-purple-100 text-purple-700',
      'On-Page': 'bg-blue-100 text-blue-700',
      'Off-Page': 'bg-pink-100 text-pink-700',
      'UI/UX Design': 'bg-indigo-100 text-indigo-700',
      'SEO': 'bg-green-100 text-green-700',
      'QA/Technical': 'bg-orange-100 text-orange-700',
      'Google Ads': 'bg-red-100 text-red-700',
      'Meta Ads': 'bg-cyan-100 text-cyan-700',
      'Others': 'bg-slate-100 text-slate-700',
      // Legacy categories for backwards compatibility
      'Technical': 'bg-purple-100 text-purple-700',
      'OnPage': 'bg-blue-100 text-blue-700',
      'OffPage': 'bg-pink-100 text-pink-700'
    };
    return colors[category] || colors['Others'];
  };

  const getContentTypeColor = getContentTypeBadgeClasses;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader className="flex flex-row items-center justify-between pr-8">
          <div className="flex items-center gap-2">
            <DialogTitle className="text-2xl">Task Details</DialogTitle>
            <TaskIdBadge taskId={task.task_id} className="text-xs" />
          </div>
          <DialogDescription className="sr-only">
            View task details, timeline, files and comments
          </DialogDescription>
          {canEdit && onEdit && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleEditClick}
              className="flex items-center gap-2 text-cyan-600 border-cyan-200 hover:bg-cyan-50 hover:text-cyan-700"
              data-testid="task-detail-edit-button"
            >
              <Pencil size={16} />
              Edit Task
            </Button>
          )}
        </DialogHeader>

        {/* Tabs — Overview hosts the existing card layout, Timeline shows the
            activity feed, Files / Comments are scaffolded placeholders. */}
        <div className="border-b border-slate-200 -mt-2">
          <nav className="flex gap-4 px-1" role="tablist" data-testid="task-modal-tabs">
            {[
              { id: 'overview',  label: 'Overview' },
              { id: 'timeline',  label: 'Timeline' },
              { id: 'files',     label: 'Files' },
              { id: 'comments',  label: commentCount === null ? 'Comments' : `Comments (${commentCount})` },
            ].map((t) => {
              const active = activeTab === t.id;
              return (
                <button
                  key={t.id}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  onClick={() => setActiveTab(t.id)}
                  data-testid={`task-modal-tab-${t.id}`}
                  className={`relative px-1 py-2.5 text-sm font-medium transition-colors ${
                    active ? 'text-cyan-600' : 'text-slate-500 hover:text-slate-700'
                  }`}
                >
                  {t.label}
                  {active && (
                    <span className="absolute inset-x-0 -bottom-px h-0.5 bg-cyan-500 rounded-full" />
                  )}
                </button>
              );
            })}
          </nav>
        </div>

        {activeTab === 'timeline' && (
          <div className="pt-4">
            <TaskTimeline taskId={task.id} users={users} />
          </div>
        )}
        {activeTab === 'files' && (
          <div className="pt-6 pb-4 text-center text-sm text-slate-500" data-testid="task-modal-files-tab">
            File attachments will live here. Coming soon.
          </div>
        )}
        {activeTab === 'comments' && (
          <div className="pt-4 pb-2" data-testid="task-modal-comments-tab">
            <TaskComments taskId={task.id} onCountChange={setCommentCount} onLinksAdded={handleLinksAdded} />
          </div>
        )}

        {activeTab === 'overview' && (
        <div className="space-y-6 pt-4">
          {/* Status Badge */}
          <div className="flex items-center gap-3 flex-wrap">
            <span className={`px-4 py-2 rounded-full text-sm font-semibold border ${getStatusColor(task.your_status)}`}>
              {task.your_status}
            </span>
            <span className={`px-3 py-1 rounded-md text-xs font-semibold ${getCategoryColor(task.category)}`}>
              {task.category}
            </span>
            {task.content_type && (
              <span className={`px-3 py-1 rounded-md text-xs font-medium ${getContentTypeColor(task.content_type)}`}>
                {task.content_type}
              </span>
            )}
            {task.developer_dependency && (
              <span className="px-3 py-1 rounded-md text-xs font-semibold bg-orange-100 text-orange-700">
                Developer Dependency
              </span>
            )}
            {task.uiux_dependency && (
              <span className="px-3 py-1 rounded-md text-xs font-semibold bg-indigo-100 text-indigo-700">
                UI/UX Dependency
              </span>
            )}
            {task.manager_approval_required && task.manager_approval_status === 'Pending' && (
              <span className="px-3 py-1 rounded-md text-xs font-semibold bg-yellow-100 text-yellow-700 border border-yellow-200">
                ⏳ Pending Manager Approval
              </span>
            )}
            {task.manager_approval_status === 'Approved' && (
              <span className="px-3 py-1 rounded-md text-xs font-semibold bg-green-100 text-green-700 border border-green-200">
                ✅ Approved
              </span>
            )}
            {task.manager_approval_status === 'Standby' && (
              <span className="px-3 py-1 rounded-md text-xs font-semibold bg-amber-100 text-amber-700 border border-amber-200">
                ⏸️ Standby
              </span>
            )}
            {task.manager_approval_status === 'Rejected' && (
              <span className="px-3 py-1 rounded-md text-xs font-semibold bg-red-100 text-red-700 border border-red-200">
                ❌ Rejected
              </span>
            )}
          </div>

          {/* Task Description */}
          <div>
            <h3 className="text-sm font-semibold text-slate-600 mb-2">Task Description</h3>
            <p className="text-lg text-slate-900 leading-relaxed bg-slate-50 p-4 rounded-lg">
              {task.task}
            </p>
          </div>

          {/* Output */}
          {task.output && (
            <div>
              <h3 className="text-sm font-semibold text-slate-600 mb-2">Output</h3>
              <p className="text-slate-800 bg-green-50 p-4 rounded-lg border border-green-200">
                {task.output}
              </p>
            </div>
          )}

          {/* Links Section - supports both old single link and new multi-link format */}
          <LinkPillList
            links={linksOverride !== null ? linksOverride : task.links}
            legacyLink={task.link}
            testIdPrefix="task-link"
          />

          {/* Details Grid */}
          <div className="grid grid-cols-2 gap-4">
            {/* Assigned To */}
            {assignedUser && (
              <div className="bg-slate-50 p-4 rounded-lg">
                <div className="flex items-center gap-2 text-slate-600 mb-1">
                  <User size={16} />
                  <span className="text-sm font-semibold">Assigned To</span>
                </div>
                <p className="text-slate-900 font-medium">{assignedUser.full_name}</p>
              </div>
            )}

            {/* Brand */}
            {task.brand && (
              <div className="bg-amber-50 p-4 rounded-lg border border-amber-200">
                <div className="flex items-center gap-2 text-amber-700 mb-1">
                  <Tag size={16} />
                  <span className="text-sm font-semibold">Brand</span>
                </div>
                <p className="text-amber-900 font-medium">{task.brand}</p>
              </div>
            )}

            {/* Start Date */}
            {task.start_date && (
              <div className="bg-slate-50 p-4 rounded-lg">
                <div className="flex items-center gap-2 text-slate-600 mb-1">
                  <Calendar size={16} />
                  <span className="text-sm font-semibold">Start Date</span>
                </div>
                <p className="text-slate-900 font-medium">
                  {format(new Date(task.start_date), 'MMM dd, yyyy')}
                </p>
              </div>
            )}

            {/* End Date */}
            {task.your_end_date && (
              <div className="bg-slate-50 p-4 rounded-lg">
                <div className="flex items-center gap-2 text-slate-600 mb-1">
                  <Calendar size={16} />
                  <span className="text-sm font-semibold">End Date</span>
                </div>
                <p className="text-slate-900 font-medium">
                  {format(new Date(task.your_end_date), 'MMM dd, yyyy')}
                </p>
              </div>
            )}

            {/* Daily Date */}
            {task.daily_date && (
              <div className="bg-slate-50 p-4 rounded-lg">
                <div className="flex items-center gap-2 text-slate-600 mb-1">
                  <Calendar size={16} />
                  <span className="text-sm font-semibold">Daily Date</span>
                </div>
                <p className="text-slate-900 font-medium">
                  {format(new Date(task.daily_date), 'MMM dd, yyyy')}
                </p>
              </div>
            )}
          </div>

          {/* Time Tracking */}
          {(task.estimated_time_mins || task.actual_time_mins) && (
            <div className="grid grid-cols-2 gap-4">
              {task.estimated_time_mins && (
                <div className="bg-slate-50 p-4 rounded-lg">
                  <div className="flex items-center gap-2 text-slate-600 mb-1">
                    <Clock size={16} />
                    <span className="text-sm font-semibold">Estimated Time</span>
                  </div>
                  <p className="text-slate-900 font-medium">{task.estimated_time_mins} minutes</p>
                </div>
              )}

              {task.actual_time_mins && (
                <div className="bg-slate-50 p-4 rounded-lg">
                  <div className="flex items-center gap-2 text-slate-600 mb-1">
                    <Clock size={16} />
                    <span className="text-sm font-semibold">Actual Time</span>
                  </div>
                  <p className="text-slate-900 font-medium">{task.actual_time_mins} minutes</p>
                </div>
              )}
            </div>
          )}

          {/* Developer Status */}
          {task.developer_dependency && (
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
              <h3 className="text-sm font-semibold text-orange-800 mb-3">Developer Information</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-orange-600 mb-1">Developer Status</p>
                  <p className="text-orange-900 font-medium">{task.developer_status || 'Pending'}</p>
                </div>
                {task.developer_end_date && (
                  <div>
                    <p className="text-xs text-orange-600 mb-1">Developer End Date</p>
                    <p className="text-orange-900 font-medium">
                      {format(new Date(task.developer_end_date), 'MMM dd, yyyy')}
                    </p>
                  </div>
                )}
              </div>
              {/* Completed By Section */}
              <div className="mt-3 pt-3 border-t border-orange-200">
                <p className="text-xs text-orange-600 mb-1">Completed By</p>
                {task.developer_completed_by && task.developer_completed_by.length > 0 ? (
                  <div className="space-y-1">
                    {task.developer_completed_by.map((completedUser, index) => (
                      <div key={completedUser.user_id || index} className="flex items-center justify-between text-sm">
                        <span className="text-orange-900 font-medium">{completedUser.user_name}</span>
                        {completedUser.completed_at && (
                          <span className="text-xs text-orange-600">
                            {format(new Date(completedUser.completed_at), 'MMM dd, yyyy HH:mm')}
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-orange-700 text-sm italic">Not Completed Yet</p>
                )}
              </div>
            </div>
          )}

          {/* UI/UX Design Status */}
          {task.uiux_dependency && (
            <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
              <h3 className="text-sm font-semibold text-indigo-800 mb-3">UI/UX Design Information</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-indigo-600 mb-1">Design Status</p>
                  <p className="text-indigo-900 font-medium">{task.design_status || 'Pending'}</p>
                </div>
                {task.design_end_date && (
                  <div>
                    <p className="text-xs text-indigo-600 mb-1">Design End Date</p>
                    <p className="text-indigo-900 font-medium">
                      {format(new Date(task.design_end_date), 'MMM dd, yyyy')}
                    </p>
                  </div>
                )}
              </div>
              {/* Completed By Section */}
              <div className="mt-3 pt-3 border-t border-indigo-200">
                <p className="text-xs text-indigo-600 mb-1">Completed By</p>
                {task.design_completed_by && task.design_completed_by.length > 0 ? (
                  <div className="space-y-1">
                    {task.design_completed_by.map((completedUser, index) => (
                      <div key={completedUser.user_id || index} className="flex items-center justify-between text-sm">
                        <span className="text-indigo-900 font-medium">{completedUser.user_name}</span>
                        {completedUser.completed_at && (
                          <span className="text-xs text-indigo-600">
                            {format(new Date(completedUser.completed_at), 'MMM dd, yyyy HH:mm')}
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-indigo-700 text-sm italic">Not Completed Yet</p>
                )}
              </div>
              {/* Manager Approval Status (UI/UX completion approval flow) */}
              {task.uiux_approval_status && (
                <div className="mt-3 pt-3 border-t border-indigo-200" data-testid="uiux-approval-status-section">
                  <p className="text-xs text-indigo-600 mb-1">Manager Approval Status</p>
                  <p className={`font-medium ${
                    task.uiux_approval_status === 'Approved' ? 'text-green-700' :
                    task.uiux_approval_status === 'Rejected' ? 'text-red-700' :
                    task.uiux_approval_status === 'Standby' ? 'text-orange-700' :
                    'text-amber-700'
                  }`}>
                    {task.uiux_approval_status}
                  </p>
                  {task.uiux_manager_notes && (
                    <p className="text-xs text-indigo-700 italic mt-1">“{task.uiux_manager_notes}”</p>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Manager Approval Information */}
          {task.manager_approval_required && (
            <div className={`border rounded-lg p-4 ${
              task.manager_approval_status === 'Approved' ? 'bg-green-50 border-green-200' :
              task.manager_approval_status === 'Rejected' ? 'bg-red-50 border-red-200' :
              task.manager_approval_status === 'Standby' ? 'bg-amber-50 border-amber-200' :
              'bg-yellow-50 border-yellow-200'
            }`}>
              <h3 className={`text-sm font-semibold mb-3 ${
                task.manager_approval_status === 'Approved' ? 'text-green-800' :
                task.manager_approval_status === 'Rejected' ? 'text-red-800' :
                task.manager_approval_status === 'Standby' ? 'text-amber-800' :
                'text-yellow-800'
              }`}>
                Manager Approval
              </h3>
              <div className="space-y-3">
                <div>
                  <p className={`text-xs mb-1 ${
                    task.manager_approval_status === 'Approved' ? 'text-green-600' :
                    task.manager_approval_status === 'Rejected' ? 'text-red-600' :
                    task.manager_approval_status === 'Standby' ? 'text-amber-600' :
                    'text-yellow-600'
                  }`}>
                    Approval Status
                  </p>
                  <p className={`font-medium ${
                    task.manager_approval_status === 'Approved' ? 'text-green-900' :
                    task.manager_approval_status === 'Rejected' ? 'text-red-900' :
                    task.manager_approval_status === 'Standby' ? 'text-amber-900' :
                    'text-yellow-900'
                  }`}>
                    {task.manager_approval_status === 'Standby' ? '⏸️ Standby' : task.manager_approval_status}
                  </p>
                </div>
                {task.manager_notes && (
                  <div>
                    <p className={`text-xs mb-1 ${
                      task.manager_approval_status === 'Approved' ? 'text-green-600' :
                      task.manager_approval_status === 'Rejected' ? 'text-red-600' :
                      task.manager_approval_status === 'Standby' ? 'text-amber-600' :
                      'text-yellow-600'
                    }`}>
                      Manager Notes
                    </p>
                    <p className={`font-medium whitespace-pre-wrap ${
                      task.manager_approval_status === 'Approved' ? 'text-green-900' :
                      task.manager_approval_status === 'Rejected' ? 'text-red-900' :
                      task.manager_approval_status === 'Standby' ? 'text-amber-900' :
                      'text-yellow-900'
                    }`}>
                      {task.manager_notes}
                    </p>
                  </div>
                )}
                
                {/* Resubmit Button for Rejected Tasks */}
                {canResubmit && (
                  <div className="pt-3 border-t border-red-200">
                    <Button
                      onClick={handleResubmitForApproval}
                      disabled={isResubmitting}
                      className="w-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white"
                      data-testid="resubmit-approval-button"
                    >
                      {isResubmitting ? (
                        <>
                          <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                          Resubmitting...
                        </>
                      ) : (
                        <>
                          <RefreshCw className="mr-2 h-4 w-4" />
                          Resubmit for Approval
                        </>
                      )}
                    </Button>
                    <p className="text-xs text-red-600 mt-2 text-center">
                      Click to resubmit this task for manager approval
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Notes */}
          {task.notes && (
            <div>
              <h3 className="text-sm font-semibold text-slate-600 mb-2">Notes</h3>
              <p className="text-slate-700 bg-slate-50 p-4 rounded-lg border border-slate-200 whitespace-pre-wrap">
                {task.notes}
              </p>
            </div>
          )}

          {/* Timestamps */}
          <div className="border-t border-slate-200 pt-4">
            <div className="grid grid-cols-2 gap-4 text-xs text-slate-500">
              {task.created_at && (
                <div>
                  <span className="font-semibold">Created:</span> {format(new Date(task.created_at), 'MMM dd, yyyy HH:mm')}
                  {task.created_by && (() => {
                    const creator = users.find(u => u.id === task.created_by);
                    return creator ? <span className="block text-slate-600 mt-1">by {creator.full_name}</span> : null;
                  })()}
                </div>
              )}
              {task.updated_at && (
                <div>
                  <span className="font-semibold">Last Updated:</span> {format(new Date(task.updated_at), 'MMM dd, yyyy HH:mm')}
                  {task.updated_by && (() => {
                    const updater = users.find(u => u.id === task.updated_by);
                    return updater ? <span className="block text-slate-600 mt-1">by {updater.full_name}</span> : null;
                  })()}
                </div>
              )}
            </div>
          </div>
        </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default TaskDetailModal;
